import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db, functions } from '../config/firebase';
import { useOrganization } from '../contexts/OrganizationContext';
import type {
  Branch,
  BranchConfig,
} from '../types/shared';
import {
  DEFAULT_BRANCHES,
  branchesForMatching,
  branchesOffered,
  getBranchById,
  getBranchByCode,
  getBranchByLegacyName,
} from '../types/shared';
import { logCallableFailure } from '../lib/logCallableFailure';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const branchKeys = {
  all: ['branches'] as const,
  config: (orgId: string) => [...branchKeys.all, 'config', orgId] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to fetch branch configuration for the current organization
 * Returns the full BranchConfig with helper functions
 */
export function useBranchConfig() {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;

  return useQuery({
    queryKey: branchKeys.config(organizationId || ''),
    queryFn: async (): Promise<BranchConfig | null> => {
      if (!organizationId) return null;

      const configRef = doc(db, 'organizations', organizationId, 'settings', 'branches');
      const snapshot = await getDoc(configRef);

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.data() as BranchConfig;
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 60, // 60 minutes – branch config is effectively static
  });
}

/**
 * Hook to get active branches as a simple array
 * Most common use case - for dropdowns and filters
 *
 * Same empty-config rule as the server (see branchesOffered): DEFAULT_BRANCHES only when there is
 * no config doc or it has no `branches` array; an existing array offers only its active entries,
 * which may be none.
 */
export function useBranches() {
  const { data: config, isLoading, error } = useBranchConfig();

  const activeBranches = branchesOffered(config);

  return {
    branches: activeBranches,
    // Every configured branch, active or not, for resolving a STORED branch value by the server's
    // branch rule (see branchesForMatching). Offer `branches`; resolve against `matchingBranches`.
    matchingBranches: branchesForMatching(config),
    config,
    isLoading,
    error,
    // Helper functions that work with the current config
    getBranchById: (branchId: string) => getBranchById(config || undefined, branchId),
    getBranchByCode: (code: string) => getBranchByCode(config || undefined, code),
    getBranchByLegacyName: (name: string) => getBranchByLegacyName(config || undefined, name),
  };
}

/**
 * Hook to seed default branches (run once during org setup or migration)
 */
export function useSeedBranches() {
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const organizationId = currentOrganization?.organizationId;

  return useMutation({
    mutationFn: async (): Promise<BranchConfig> => {
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const now = Timestamp.now();
      const branches: Branch[] = DEFAULT_BRANCHES.map((b) => ({
        ...b,
        createdAt: now,
        updatedAt: now,
      }));

      const config: BranchConfig = {
        branches,
        defaultBranchId: 'seaside',
      };

      const fn = httpsCallable(functions, 'shared_saveBranches');
      await fn({ organizationId, action: 'seed', config });

      return config;
    },
    onSuccess: () => {
      toast.success('Branches initialized');
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: branchKeys.config(organizationId) });
      }
    },
    onError: (error) => {
      toast.error('Failed to initialize branches');
      logCallableFailure('Seed branches error:', error);
    },
  });
}

/**
 * Hook to update a branch
 */
/**
 * The server refuses a default branch that is not active (slice E delta review G10), so when an
 * edit leaves the default inactive, the default moves to the first active branch (by sort order).
 * With no active branch at all it is left as is, and the server refuses the save.
 */
function withActiveDefault(config: BranchConfig): BranchConfig {
  const current = config.branches.find((b) => b.branchId === config.defaultBranchId);
  if (!config.defaultBranchId || current?.isActive === true) return config;
  const next = [...config.branches]
    .filter((b) => b.isActive === true)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))[0];
  return next ? { ...config, defaultBranchId: next.branchId } : config;
}

export function useUpdateBranch() {
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const organizationId = currentOrganization?.organizationId;
  const orgId = organizationId || '';

  return useMutation({
    mutationFn: async ({
      branchId,
      updates,
    }: {
      branchId: string;
      updates: Partial<Omit<Branch, 'branchId' | 'createdAt'>>;
    }): Promise<void> => {
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const configRef = doc(db, 'organizations', organizationId, 'settings', 'branches');
      const snapshot = await getDoc(configRef);

      if (!snapshot.exists()) {
        throw new Error('Branch configuration not found');
      }

      const config = snapshot.data() as BranchConfig;
      const branchIndex = config.branches.findIndex((b) => b.branchId === branchId);

      if (branchIndex === -1) {
        throw new Error(`Branch ${branchId} not found`);
      }

      const updatedBranches = config.branches.map((b, i) =>
        i === branchIndex ? { ...b, ...updates, updatedAt: Timestamp.now() } : b
      );

      const fn = httpsCallable(functions, 'shared_saveBranches');
      await fn({ organizationId, action: 'update', config: withActiveDefault({ ...config, branches: updatedBranches }) });
    },
    onMutate: async ({ branchId, updates }) => {
      await queryClient.cancelQueries({ queryKey: branchKeys.config(orgId) });
      const previousConfig = queryClient.getQueryData<BranchConfig>(branchKeys.config(orgId));
      if (previousConfig) {
        queryClient.setQueryData<BranchConfig>(
          branchKeys.config(orgId),
          {
            ...previousConfig,
            branches: previousConfig.branches.map((b) =>
              b.branchId === branchId ? { ...b, ...updates, updatedAt: Timestamp.now() } : b
            ),
          },
        );
      }
      return { previousConfig };
    },
    onSuccess: () => {
      toast.success('Branch updated');
    },
    onError: (_error, _vars, context) => {
      if (context?.previousConfig) {
        queryClient.setQueryData(branchKeys.config(orgId), context.previousConfig);
      }
      toast.error('Failed to update branch');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: branchKeys.config(orgId) });
    },
  });
}

/**
 * Hook to add a new branch
 */
export function useAddBranch() {
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const organizationId = currentOrganization?.organizationId;
  const orgId = organizationId || '';

  return useMutation({
    mutationFn: async (
      newBranch: Omit<Branch, 'branchId' | 'createdAt' | 'updatedAt'>
    ): Promise<Branch> => {
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const configRef = doc(db, 'organizations', organizationId, 'settings', 'branches');
      const snapshot = await getDoc(configRef);

      const now = Timestamp.now();
      const branch: Branch = {
        ...newBranch,
        branchId: newBranch.code, // Use code as ID for simplicity
        createdAt: now,
        updatedAt: now,
      };

      if (!snapshot.exists()) {
        // No config doc means every reader is using DEFAULT_BRANCHES today. Writing a one-branch
        // list would silently retire Seaside / Tracy / Reno (slice E review F8c), so the first save
        // writes the defaults the org was already using plus the new branch.
        if (DEFAULT_BRANCHES.some((b) => b.branchId === branch.branchId || b.code === branch.code)) {
          throw new Error(`Branch with code "${branch.code}" already exists`);
        }
        const config: BranchConfig = {
          branches: [
            ...DEFAULT_BRANCHES.map((b) => ({ ...b, createdAt: now, updatedAt: now })),
            branch,
          ],
          defaultBranchId: 'seaside',
        };
        const fn = httpsCallable(functions, 'shared_saveBranches');
        await fn({ organizationId, action: 'add', config });
      } else {
        // Add to existing config
        const config = snapshot.data() as BranchConfig;

        // Check for duplicate
        if (config.branches.some((b) => b.branchId === branch.branchId || b.code === branch.code)) {
          throw new Error(`Branch with code "${branch.code}" already exists`);
        }

        const fn = httpsCallable(functions, 'shared_saveBranches');
        await fn({ organizationId, action: 'add', config: { ...config, branches: [...config.branches, branch] } });
      }

      return branch;
    },
    onMutate: async (newBranch) => {
      await queryClient.cancelQueries({ queryKey: branchKeys.config(orgId) });
      const previousConfig = queryClient.getQueryData<BranchConfig>(branchKeys.config(orgId));
      if (previousConfig) {
        const now = Timestamp.now();
        const optimisticBranch: Branch = {
          ...newBranch,
          branchId: newBranch.code,
          createdAt: now,
          updatedAt: now,
        };
        queryClient.setQueryData<BranchConfig>(
          branchKeys.config(orgId),
          {
            ...previousConfig,
            branches: [...previousConfig.branches, optimisticBranch],
          },
        );
      }
      return { previousConfig };
    },
    onSuccess: (branch) => {
      toast.success(`Branch "${branch.name}" added`);
    },
    onError: (_error, _vars, context) => {
      if (context?.previousConfig) {
        queryClient.setQueryData(branchKeys.config(orgId), context.previousConfig);
      }
      toast.error('Failed to add branch');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: branchKeys.config(orgId) });
    },
  });
}

/**
 * Hook to deactivate a branch (soft delete)
 * Simplified wrapper around useUpdateBranch
 */
export function useDeactivateBranch() {
  const updateBranch = useUpdateBranch();

  return {
    ...updateBranch,
    mutate: (branchId: string) =>
      updateBranch.mutate({ branchId, updates: { isActive: false } }),
    mutateAsync: (branchId: string) =>
      updateBranch.mutateAsync({ branchId, updates: { isActive: false } }),
  };
}

/**
 * Hook to reactivate a branch
 * Simplified wrapper around useUpdateBranch
 */
export function useReactivateBranch() {
  const updateBranch = useUpdateBranch();

  return {
    ...updateBranch,
    mutate: (branchId: string) =>
      updateBranch.mutate({ branchId, updates: { isActive: true } }),
    mutateAsync: (branchId: string) =>
      updateBranch.mutateAsync({ branchId, updates: { isActive: true } }),
  };
}

// ============================================================================
// UTILITY FUNCTIONS (for use outside of React components)
// ============================================================================

/**
 * Fetch branch config directly (for use in non-hook contexts like cloud functions)
 */
export async function fetchBranchConfig(organizationId: string): Promise<BranchConfig | null> {
  const configRef = doc(db, 'organizations', organizationId, 'settings', 'branches');
  const snapshot = await getDoc(configRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as BranchConfig;
}

/**
 * Resolve a legacy branch name to a branch ID
 * Used during migration from hardcoded Branch type to BranchConfig
 */
export async function resolveBranchNameToId(
  organizationId: string,
  legacyName: string
): Promise<string | null> {
  const config = await fetchBranchConfig(organizationId);
  if (!config) return null;

  const branch = getBranchByLegacyName(config, legacyName);
  return branch?.branchId || null;
}
